import { Alert, Box, Typography } from "@mui/material";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as userService from "../../../models/services/userService";
import { IUserGroupListItem, UserGroupListItem } from "ui/Scripts/source/models/userGroup/userGroupListItem";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { SelectUserGroupDialogWithCompanySelect } from "source/components/userGroup/SelectUserGroupDialogWithCompanySelect";
import { IListValueLineData } from "source/components/framework/List/ListLine";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { ISessionState } from "../../../models/session/sessionState";
import { ICodeTitle } from "source/models/common/codeTitle";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { TCodeCompanyCode } from "ui/Scripts/source/models/common/codeCompanyCode";

export interface IUserEditDialogUserGroupListProp {
    isReadOnly: boolean;
    sessionState: ISessionState;
    userId: string;
    companyId: string;
    isQrCodeAccount: boolean;
    userGroups: IUserGroupListItem[];
    companies: ICompanyMenuItem[];
    companiesRequireUserGroups: ICodeTitle[];
    onRemoved: (ids: string[]) => void;
    onAdded: (userGroups: IUserGroupListItem[]) => void;
}

interface IUserEditDialogUserGroupListState {
    // List
    isLoading: boolean;
    userGroups: IUserGroupListItem[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];

    // Dialog
    showSelectUserGroupDialog: boolean;
    selectUserGroupDialogCompanies: ICompanyMenuItem[];
}

export class UserEditDialogUserGroupList extends React.Component<IUserEditDialogUserGroupListProp, IUserEditDialogUserGroupListState> {
    constructor(props: IUserEditDialogUserGroupListProp) {
        super(props);
        this.state = {
            isLoading: false,
            userGroups: props.userGroups ? props.userGroups.slice(0) : [],
            filter: "",
            sortColumn: "name",
            sortOrderIsAsc: true,
            selectedId: null,
            checkedIds: [],
            showSelectUserGroupDialog: false,
            selectUserGroupDialogCompanies: []
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const props = this.props;
        let userGroups = props.userGroups ? props.userGroups.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            userGroups = userGroups.filter(i => i.name.toLowerCase().indexOf(lowFilter) > -1 || i.comment.toLowerCase().indexOf(lowFilter) > -1);
        }
        UserGroupListItem.sortUserGroupListItems(userGroups, column, asc);
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            userGroups
        });
    };

    componentDidUpdate(prevProps: IUserEditDialogUserGroupListProp, prevState: IUserEditDialogUserGroupListState) {
        const props = this.props;
        const state = this.state;
        if (Base.equalStrArrays(prevProps.userGroups ? prevProps.userGroups.map(i => i.id) : [], props.userGroups ? props.userGroups.map(i => i.id) : [])) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    };

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: IUserEditDialogUserGroupListProp, userGroupIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.userId);
        result.append("companyId", props.companyId);
        result.append("userGroupIds", userGroupIds);
        return result;
    };

    handleRemove = async () => {
        const obj = this;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const saveData = this.getRemoveSaveData(this.props, selectedIds);
        // Call server
        const result = await AppUtils.callService(() => userService.userEditRemoveUserGroups(saveData.obj));
        if (!result) return;
        obj.props.onRemoved(selectedIds);
    };
    
    handleAdd = () => {
        const props = this.props;
        this.setState({
            showSelectUserGroupDialog: true,
            selectUserGroupDialogCompanies: props.sessionState.isSuperAdmin
                ? props.companies
                : props.companies.filter(i => props.sessionState.allowedCompanies.find(j => j.code === i.code))
        });
    };

    getAddSaveData = (props: IUserEditDialogUserGroupListProp, userGroups: TCodeCompanyCode[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.userId);
        result.append("companyId", props.companyId);
        result.append("userGroups", userGroups);
        return result;
    };

    handleSelectUserGroupDialogOk = async (userGroups: IUserGroupListItem[]) => {
        const props = this.props;
        if (!userGroups || userGroups.length < 1) return;
        this.setState({
            showSelectUserGroupDialog: false
        });
        const saveData = this.getAddSaveData(props, userGroups.map(i => ({ companyCode: i.companyCode, code: i.code })));
        // Call server
        const result = await AppUtils.callService(() => userService.userEditAddUserGroups(saveData.obj));
        if (!result) return;
        props.onAdded(userGroups);
    };

    handleSelectUserGroupDialogCancel = () => {
        this.setState({
            showSelectUserGroupDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const multipleCompaniesInUse = Base.getUniqueStringItems(state.userGroups.map(i => i.companyCode)).length > 1;
        return (
            <>
                <VerticalContainer
                    before={<>
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter} />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddUserGroup}
                                    removeTooltip={Translations.RemoveUserGroup}
                                    addDisabled={props.isReadOnly}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                        {!!props.isReadOnly &&
                            <Alert severity="info">{Translations.NewUserMustBeSavedBeforeUserGroupsCanBeAdded}</Alert>
                        }
                    </>}
                    main={<>
                        {!props.isReadOnly &&
                            <List
                                intialized={true}
                                isLoading={state.isLoading}
                                hasMore={false}
                                height="100%"
                                globalLoadingInUse={false}
                                header={{
                                    columns: [
                                        { size: 4, column: "name", content: Translations.Name },
                                        { size: 4, column: "comment", content: Translations.Description },
                                        { size: 4, column: "level", content: Translations.AuthorizationLevel }
                                    ]
                                }}
                                lines={(() => {
                                    const lines: IListValueLineData[] = [];
                                    for (const company of props.companies) {
                                        const companyUserGroups = state.userGroups.filter(i => i.companyCode === company.code);
                                        if (companyUserGroups.length < 1) continue;
                                        //Company row
                                        if (multipleCompaniesInUse) {
                                            lines.push({
                                                columns: [
                                                    {
                                                        size: 12,
                                                        content:
                                                            <Box display="flex" alignItems="center">
                                                                <AvatarImage
                                                                    name={company.name}
                                                                    image={company.logo}
                                                                    tooltip={company.name}
                                                                    marginRight=".5rem"
                                                                />
                                                                <Typography>{company.name}</Typography>
                                                            </Box>
                                                    }
                                                ],
                                                id: company.id,
                                                unselectable: true,
                                            });
                                        }
                                        //UserGroup rows
                                        for (const userGroup of companyUserGroups) {
                                            lines.push({
                                                columns: [
                                                    { size: 4, content: userGroup.name },
                                                    { size: 4, content: userGroup.comment },
                                                    { size: 4, content: userGroup.levelStr }],
                                                id: userGroup.id
                                            });
                                        }
                                    }
                                    return lines;
                                })()}
                                noDataMessage={Translations.AddUserGroups}
                                sortColumn={state.sortColumn}
                                sortOrderIsAsc={state.sortOrderIsAsc}
                                selectedId={state.selectedId}
                                checkedIds={state.checkedIds}
                                onSetSelectedIds={this.handleSetSelectedIds}
                                onSearchLines={this.handleSearchLines}
                            />
                        }
                    </>}
                />
                {state.showSelectUserGroupDialog &&
                    <SelectUserGroupDialogWithCompanySelect
                        sessionState={props.sessionState}
                        companies={state.selectUserGroupDialogCompanies}
                        multiSelect={true}
                        ignoreUserGroups={props.userGroups.map(i => {
                            return { companyCode: i.companyCode, code: i.code } as TCodeCompanyCode;
                        })}
                        requireUserGroups={props.companiesRequireUserGroups.map(i => {
                            return { companyCode: i.code, code: i.title } as TCodeCompanyCode;
                        })}
                        ignoreQrCodeUserGroups={!props.isQrCodeAccount}
                        requireQrCodeUserGroups={props.isQrCodeAccount}
                        onOk={this.handleSelectUserGroupDialogOk}
                        onCancel={this.handleSelectUserGroupDialogCancel}
                    />
                }
            </>
        );
    }
}